import React, { useState, useEffect, useMemo } from 'react';
import { authService } from '../../services/authService';
// Database uses asset_name (not name) and snake_case fields
interface DBAsset {
  id: number;
  asset_name: string;
  description?: string;
  asset_type: string;
  classification: string;
  owner?: string;
  location?: string;
  criticality?: string;
  status?: string;
  value?: number;
}
import { grcService } from '../../services/grcService';
import { 
  Plus, Edit, Trash2, Server, Database, Users, FileText, Building, Cpu,
  Download, RefreshCw, Search, Filter, ChevronDown, ChevronUp,
  HardDrive, Globe, Lock, Unlock, Shield, Laptop, Cloud, Network,
  Smartphone, Package, Layers
} from 'lucide-react';

interface UnifiedAssetInventoryProps {
  userRole: 'admin' | 'editor' | 'viewer';
  onAssetSelect?: (asset: DBAsset) => void;
}

type AssetType = 'hardware' | 'software' | 'data' | 'people' | 'process' | 'facility' | 'network' | 'service' | 'other';
type Classification = 'public' | 'internal' | 'confidential' | 'restricted' | 'top_secret';

export const UnifiedAssetInventory: React.FC<UnifiedAssetInventoryProps> = ({
  userRole,
  onAssetSelect
}) => {
  const [assets, setAssets] = useState<DBAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<AssetType | 'all'>('all');
  const [classificationFilter, setClassificationFilter] = useState<Classification | 'all'>('all');
  const [expandedAsset, setExpandedAsset] = useState<number | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingAsset, setEditingAsset] = useState<DBAsset | null>(null);

  // Check permissions (section-based RBAC)
  const canEdit = authService.hasPermission('assets', 'edit') || authService.isGlobalAdmin() || authService.isOrgAdmin();

  useEffect(() => {
    loadAssets();
  }, []);

  const loadAssets = async () => {
    setLoading(true);
    const data = await grcService.getGlobalAssets() as unknown as DBAsset[];
    setAssets(data);
    setLoading(false);
  };

  const filteredAssets = useMemo(() => {
    let result = assets;

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(a =>
        a.asset_name?.toLowerCase().includes(query) ||
        a.description?.toLowerCase().includes(query) ||
        a.owner?.toLowerCase().includes(query) ||
        a.location?.toLowerCase().includes(query)
      );
    }

    if (typeFilter !== 'all') {
      result = result.filter(a => a.asset_type === typeFilter);
    }

    if (classificationFilter !== 'all') {
      result = result.filter(a => a.classification === classificationFilter);
    }

    return result;
  }, [assets, searchQuery, typeFilter, classificationFilter]);

  // Group by type for summary
  const stats = useMemo(() => {
    const byType: Record<string, number> = {};
    const byClassification: Record<string, number> = {};
    
    assets.forEach(a => {
      byType[a.asset_type || 'other'] = (byType[a.asset_type || 'other'] || 0) + 1;
      byClassification[a.classification || 'internal'] = (byClassification[a.classification || 'internal'] || 0) + 1;
    });

    return {
      total: assets.length,
      byType,
      byClassification,
      critical: assets.filter(a => a.criticality === 'Critical' || a.criticality === 'High').length
    };
  }, [assets]);

  const handleDelete = async (id: number) => {
    if (!confirm('Are you sure you want to delete this asset?')) return;
    
    const success = await grcService.deleteAsset(id);
    if (success) {
      await loadAssets();
    }
  };

  const getTypeIcon = (type: string) => {
    const iconClass = "w-5 h-5";
    switch (type) {
      case 'hardware': return <HardDrive className={iconClass} />;
      case 'software': return <Cpu className={iconClass} />;
      case 'data': return <Database className={iconClass} />;
      case 'people': return <Users className={iconClass} />;
      case 'process': return <Layers className={iconClass} />;
      case 'facility': return <Building className={iconClass} />;
      case 'network': return <Network className={iconClass} />;
      case 'service': return <Cloud className={iconClass} />;
      default: return <Package className={iconClass} />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'hardware': return 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400';
      case 'software': return 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400';
      case 'data': return 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400';
      case 'people': return 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400';
      case 'process': return 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400';
      case 'facility': return 'bg-stone-100 dark:bg-stone-900/30 text-stone-600 dark:text-stone-400';
      case 'network': return 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-600 dark:text-cyan-400';
      case 'service': return 'bg-pink-100 dark:bg-pink-900/30 text-pink-600 dark:text-pink-400';
      default: return 'bg-gray-100 dark:bg-gray-900/30 text-gray-600 dark:text-gray-400';
    }
  };

  const getClassificationBadge = (classification: string) => {
    switch (classification) {
      case 'top_secret': return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 border-red-200';
      case 'restricted': return 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 border-orange-200';
      case 'confidential': return 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border-amber-200';
      case 'internal': return 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-200';
      case 'public': return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border-green-200';
      default: return 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 border-gray-200';
    }
  };

  const getCriticalityBadge = (criticality: string) => {
    switch (criticality) {
      case 'Critical': return 'bg-red-100 text-red-700 border-red-200';
      case 'High': return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'Medium': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'Low': return 'bg-green-100 text-green-700 border-green-200';
      default: return 'bg-gray-100 text-gray-600 border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-8 h-8 border-4 border-gray-200 border-t-indigo-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-indigo-100 dark:bg-indigo-900/30 rounded-xl flex items-center justify-center">
            <Server className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Unified Asset Inventory</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">Assets across all frameworks and standards</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={loadAssets}
            className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
          {canEdit && (
            <button
              onClick={() => setShowAddModal(true)}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Asset
            </button>
          )}
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
          <div className="text-2xl font-bold text-gray-900 dark:text-white">{stats.total}</div>
          <div className="text-xs text-gray-500">Total Assets</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
          <div className="text-2xl font-bold text-red-600">{stats.critical}</div>
          <div className="text-xs text-gray-500">Critical/High</div>
        </div>
        {Object.entries(stats.byType).slice(0, 4).map(([type, count]) => (
          <div 
            key={type}
            onClick={() => setTypeFilter(typeFilter === type ? 'all' : type as AssetType)}
            className={`bg-white dark:bg-gray-800 rounded-lg p-4 border cursor-pointer transition-colors ${
              typeFilter === type ? 'border-indigo-500 ring-2 ring-indigo-200' : 'border-gray-200 dark:border-gray-700 hover:border-indigo-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <div className={`p-1.5 rounded ${getTypeColor(type)}`}>
                {getTypeIcon(type)}
              </div>
              <div>
                <div className="text-xl font-bold text-gray-900 dark:text-white">{count}</div>
                <div className="text-xs text-gray-500 capitalize">{type}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Search & Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search assets..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
          />
        </div>
        
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value as AssetType | 'all')}
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
        >
          <option value="all">All Types</option>
          <option value="hardware">Hardware</option>
          <option value="software">Software</option>
          <option value="data">Data</option>
          <option value="people">People</option>
          <option value="process">Process</option>
          <option value="facility">Facility</option>
          <option value="network">Network</option>
          <option value="service">Service</option>
        </select>

        <select
          value={classificationFilter}
          onChange={(e) => setClassificationFilter(e.target.value as Classification | 'all')}
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
        >
          <option value="all">All Classifications</option>
          <option value="public">Public</option>
          <option value="internal">Internal</option>
          <option value="confidential">Confidential</option>
          <option value="restricted">Restricted</option>
          <option value="top_secret">Top Secret</option>
        </select>
        
        {(typeFilter !== 'all' || classificationFilter !== 'all') && (
          <button
            onClick={() => { setTypeFilter('all'); setClassificationFilter('all'); }}
            className="text-sm text-indigo-600 hover:text-indigo-800"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Asset Grid */}
      {filteredAssets.length === 0 ? (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
          <Server className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>{assets.length === 0 ? 'No assets registered yet' : 'No assets match your filters'}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredAssets.map(asset => (
            <div
              key={asset.id}
              onClick={() => canEdit && setEditingAsset(asset)}
              className={`bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden transition-all hover:shadow-lg ${canEdit ? 'cursor-pointer hover:scale-[1.02]' : ''}`}
            >
              <div className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className={`p-2 rounded-lg ${getTypeColor(asset.asset_type)}`}>
                    {getTypeIcon(asset.asset_type)}
                  </div>
                  <div className="flex items-center gap-2">
                    {asset.criticality && (
                      <span className={`px-2 py-0.5 rounded text-xs font-medium border ${getCriticalityBadge(asset.criticality)}`}>
                        {asset.criticality}
                      </span>
                    )}
                    <span className={`px-2 py-0.5 rounded text-xs font-medium border ${getClassificationBadge(asset.classification)}`}>
                      {asset.classification?.replace('_', ' ')}
                    </span>
                  </div>
                </div>

                <h3 className="font-semibold text-gray-900 dark:text-white mb-1">{asset.asset_name}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{asset.description}</p>

                {/* Details Grid - Always Visible */}
                <div className="grid grid-cols-2 gap-2 p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg text-xs mb-3">
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Owner:</span>
                    <span className="ml-1 font-medium text-gray-700 dark:text-gray-300">{asset.owner || 'Unassigned'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Location:</span>
                    <span className="ml-1 font-medium text-gray-700 dark:text-gray-300">{asset.location || 'N/A'}</span>
                  </div>
                  {asset.value && (
                    <div className="col-span-2">
                      <span className="text-gray-500 dark:text-gray-400">Value:</span>
                      <span className="ml-1 font-semibold text-green-600 dark:text-green-400">${asset.value.toLocaleString()}</span>
                    </div>
                  )}
                </div>

                {/* Action Footer */}
                {canEdit && (
                  <div className="flex items-center justify-between pt-2 border-t border-gray-100 dark:border-gray-600">
                    <span className="text-xs text-gray-400 flex items-center gap-1">
                      <Edit className="w-3 h-3" />
                      Click to edit
                    </span>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDelete(asset.id); }}
                      className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1 px-2 py-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                    >
                      <Trash2 className="w-3 h-3" />
                      Delete
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Summary Footer */}
      <div className="text-sm text-gray-500 dark:text-gray-400 text-center">
        Showing {filteredAssets.length} of {assets.length} assets across all frameworks
      </div>

      {/* Add/Edit Asset Modal */}
      {(showAddModal || editingAsset) && (
        <AssetModal
          asset={editingAsset}
          onClose={() => {
            setShowAddModal(false);
            setEditingAsset(null);
          }}
          onSave={async (assetData) => {
            try {
              if (editingAsset) {
                await grcService.updateAsset({ ...assetData, id: editingAsset.id });
              } else {
                await grcService.createAsset(assetData as any);
              }
              await loadAssets();
              setShowAddModal(false);
              setEditingAsset(null);
            } catch (e) {
              console.error('Failed to save asset:', e);
            }
          }}
        />
      )}
    </div>
  );
};

// Asset Modal Component
interface AssetModalProps {
  asset: DBAsset | null;
  onClose: () => void;
  onSave: (data: Partial<DBAsset>) => Promise<void>;
}

const AssetModal: React.FC<AssetModalProps> = ({ asset, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    asset_name: asset?.asset_name || '',
    description: asset?.description || '',
    asset_type: asset?.asset_type || 'hardware',
    classification: asset?.classification || 'internal',
    owner: asset?.owner || '',
    location: asset?.location || '',
    criticality: asset?.criticality || 'Medium',
    value: asset?.value || 0
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.asset_name.trim()) return;
    
    setSaving(true);
    await onSave(formData);
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-200 dark:border-gray-700">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            {asset ? 'Edit Asset' : 'Add New Asset'}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Asset Name *
            </label>
            <input
              type="text"
              value={formData.asset_name}
              onChange={(e) => setFormData({ ...formData, asset_name: e.target.value })}
              className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="Enter asset name"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="Describe the asset"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Asset Type
              </label>
              <select
                value={formData.asset_type}
                onChange={(e) => setFormData({ ...formData, asset_type: e.target.value })}
                className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="hardware">Hardware</option>
                <option value="software">Software</option>
                <option value="data">Data</option>
                <option value="people">People</option>
                <option value="process">Process</option>
                <option value="facility">Facility</option>
                <option value="network">Network</option>
                <option value="service">Service</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Classification
              </label>
              <select
                value={formData.classification}
                onChange={(e) => setFormData({ ...formData, classification: e.target.value })}
                className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="public">Public</option>
                <option value="internal">Internal</option>
                <option value="confidential">Confidential</option>
                <option value="restricted">Restricted</option>
                <option value="top_secret">Top Secret</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Owner
              </label>
              <input
                type="text"
                value={formData.owner}
                onChange={(e) => setFormData({ ...formData, owner: e.target.value })}
                className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="Asset owner"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Location
              </label>
              <input
                type="text"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="Asset location"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Criticality
              </label>
              <select
                value={formData.criticality}
                onChange={(e) => setFormData({ ...formData, criticality: e.target.value })}
                className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="Critical">Critical</option>
                <option value="High">High</option>
                <option value="Medium">Medium</option>
                <option value="Low">Low</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Value ($)
              </label>
              <input
                type="number"
                value={formData.value}
                onChange={(e) => setFormData({ ...formData, value: parseInt(e.target.value) || 0 })}
                className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="Asset value"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !formData.asset_name.trim()}
              className="px-6 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                asset ? 'Update Asset' : 'Add Asset'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
